import { useState, useRef, useEffect, useCallback } from "react";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const hideScrollbarStyle = `
.tp-no-scrollbar::-webkit-scrollbar { display: none; }
`;

interface TimePickerProps {
  value: string; // "HH:mm" 24h format
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
}

function parse24(value: string) {
  const [hStr, mStr] = value.split(":");
  let h = parseInt(hStr, 10) || 0;
  const m = parseInt(mStr, 10) || 0;
  const period: "AM" | "PM" = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return { hour: h12, minute: m, period };
}

function to24(hour: number, minute: number, period: "AM" | "PM") {
  let h = hour;
  if (period === "AM" && h === 12) h = 0;
  else if (period === "PM" && h !== 12) h += 12;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function formatTime12(value: string) {
  if (!value) return "";
  const p = parse24(value);
  return `${p.hour}:${String(p.minute).padStart(2, "0")} ${p.period}`;
}

/** Parse user-typed time string like "9:30 AM", "14:00", "930pm" etc. */
function parseTypedTime(raw: string): { hour: number; minute: number; period: "AM" | "PM" } | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return null;

  let period: "AM" | "PM" | null = null;
  let cleaned = s;
  if (cleaned.endsWith("am")) { period = "AM"; cleaned = cleaned.slice(0, -2); }
  else if (cleaned.endsWith("pm")) { period = "PM"; cleaned = cleaned.slice(0, -2); }
  else if (cleaned.endsWith("a")) { period = "AM"; cleaned = cleaned.slice(0, -1); }
  else if (cleaned.endsWith("p")) { period = "PM"; cleaned = cleaned.slice(0, -1); }

  let h: number, m: number;
  if (cleaned.includes(":")) {
    const [hh, mm] = cleaned.split(":");
    h = parseInt(hh, 10);
    m = parseInt(mm, 10) || 0;
  } else {
    const n = parseInt(cleaned, 10);
    if (isNaN(n)) return null;
    if (n <= 12) { h = n; m = 0; }
    else if (n <= 99) { h = n; m = 0; } // e.g. "13" → 1 PM
    else { h = Math.floor(n / 100); m = n % 100; } // e.g. "930" → 9:30
  }

  if (isNaN(h) || isNaN(m) || m < 0 || m > 59) return null;

  // If no AM/PM specified, treat as 24h if h > 12
  if (!period) {
    if (h === 0) { period = "AM"; h = 12; }
    else if (h > 12) { period = "PM"; h = h - 12; }
    else if (h === 12) { period = "PM"; }
    else { period = "AM"; }
  } else {
    if (h > 12) h = h % 12;
    if (h === 0) h = 12;
  }

  if (h < 1 || h > 12) return null;
  return { hour: h, minute: m, period };
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const PERIODS: ("AM" | "PM")[] = ["AM", "PM"];

const ITEM_H = 32;
const VISIBLE = 7;
const DRAG_THRESHOLD = 4;
const REPEATS = 20; // repeat items this many times for seamless looping

function ScrollColumn<T>({
  items,
  selected,
  onSelect,
  format: fmt,
  loop = true,
}: {
  items: T[];
  selected: T;
  onSelect: (v: T) => void;
  format?: (v: T) => string;
  loop?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const scrollTimer = useRef<ReturnType<typeof setTimeout>>();
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const dragStartY = useRef(0);
  const scrollStartTop = useRef(0);
  const isResetting = useRef(false);

  const pad = Math.floor(VISIBLE / 2);
  const display = fmt || ((v: T) => String(v));
  const n = items.length;

  // For looping: repeat items REPEATS times; center block is at index REPEATS/2
  const centerBlock = Math.floor(REPEATS / 2);
  const centerOffset = pad * ITEM_H;
  const totalItems = loop ? n * REPEATS : n;

  const getScrollTop = (blockIdx: number, itemIdx: number) =>
    (blockIdx * n + itemIdx) * ITEM_H;

  const scrollToSelected = useCallback(
    (smooth = false) => {
      if (!ref.current) return;
      const idx = items.indexOf(selected);
      if (idx < 0) return;
      if (loop) {
        const target = getScrollTop(centerBlock, idx) - centerOffset;
        ref.current.scrollTo({ top: target, behavior: smooth ? "smooth" : "auto" });
      } else {
        ref.current.scrollTo({ top: idx * ITEM_H, behavior: smooth ? "smooth" : "auto" });
      }
    },
    [selected, items, loop, n, centerBlock, centerOffset]
  );

  useEffect(() => {
    scrollToSelected(false);
  }, [scrollToSelected]);

  // Re-center silently when scroll gets too far from center
  const recenterIfNeeded = useCallback(() => {
    if (!ref.current || !loop || isResetting.current) return;
    const scrollTop = ref.current.scrollTop;
    const totalHeight = totalItems * ITEM_H;
    const lowerBound = n * 2 * ITEM_H;
    const upperBound = totalHeight - n * 3 * ITEM_H;

    if (scrollTop < lowerBound || scrollTop > upperBound) {
      isResetting.current = true;
      const rawIdx = Math.round((scrollTop + centerOffset) / ITEM_H);
      const itemIdx = ((rawIdx % n) + n) % n;
      const newTop = getScrollTop(centerBlock, itemIdx) - centerOffset;
      ref.current.scrollTop = newTop;
      isResetting.current = false;
    }
  }, [loop, n, totalItems, centerBlock, centerOffset]);

  const snapToNearest = useCallback(() => {
    if (!ref.current) return;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      if (!ref.current) return;
      if (loop) {
        const rawIdx = Math.round((ref.current.scrollTop + centerOffset) / ITEM_H);
        const itemIdx = ((rawIdx % n) + n) % n;
        const targetTop = rawIdx * ITEM_H - centerOffset;
        ref.current.scrollTo({ top: targetTop, behavior: "smooth" });
        onSelect(items[itemIdx]);
        setTimeout(recenterIfNeeded, 300);
      } else {
        const rawIdx = Math.round(ref.current.scrollTop / ITEM_H);
        const clamped = Math.max(0, Math.min(rawIdx, n - 1));
        ref.current.scrollTo({ top: clamped * ITEM_H, behavior: "smooth" });
        onSelect(items[clamped]);
      }
    }, 60);
  }, [items, n, loop, onSelect, recenterIfNeeded, centerOffset]);

  const handleScroll = () => {
    if (isDragging.current || isResetting.current) return;
    snapToNearest();
  };

  // Mouse drag
  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    didDrag.current = false;
    dragStartY.current = e.clientY;
    scrollStartTop.current = ref.current?.scrollTop || 0;
    e.preventDefault();

    const handleMouseMove = (ev: MouseEvent) => {
      if (!ref.current || !isDragging.current) return;
      const dy = dragStartY.current - ev.clientY;
      if (Math.abs(dy) > DRAG_THRESHOLD) didDrag.current = true;
      ref.current.scrollTop = scrollStartTop.current + dy;
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      snapToNearest();
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Touch drag
  const handleTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true;
    didDrag.current = false;
    dragStartY.current = e.touches[0].clientY;
    scrollStartTop.current = ref.current?.scrollTop || 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!ref.current || !isDragging.current) return;
    const dy = dragStartY.current - e.touches[0].clientY;
    if (Math.abs(dy) > DRAG_THRESHOLD) didDrag.current = true;
    ref.current.scrollTop = scrollStartTop.current + dy;
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    snapToNearest();
  };

  const handleItemClick = (itemIdx: number) => {
    if (didDrag.current) return;
    onSelect(items[itemIdx]);
    if (loop) {
      const scrollTop = ref.current?.scrollTop || 0;
      const centerRawIdx = Math.round((scrollTop + centerOffset) / ITEM_H);
      const currentBlock = Math.floor(centerRawIdx / n);
      const target = (currentBlock * n + itemIdx) * ITEM_H - centerOffset;
      ref.current?.scrollTo({ top: target, behavior: "smooth" });
    } else {
      ref.current?.scrollTo({ top: itemIdx * ITEM_H, behavior: "smooth" });
    }
  };

  // Wheel handler for smooth single-item steps
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (!ref.current) return;
    const dir = e.deltaY > 0 ? 1 : -1;

    if (loop) {
      const currentIdx = Math.round((ref.current.scrollTop + centerOffset) / ITEM_H);
      const nextIdx = currentIdx + dir;
      ref.current.scrollTo({ top: nextIdx * ITEM_H - centerOffset, behavior: "smooth" });
      const itemIdx = ((nextIdx % n) + n) % n;
      onSelect(items[itemIdx]);
      setTimeout(recenterIfNeeded, 300);
    } else {
      const currentIdx = Math.round(ref.current.scrollTop / ITEM_H);
      const nextIdx = currentIdx + dir;
      const clamped = Math.max(0, Math.min(nextIdx, n - 1));
      ref.current.scrollTo({ top: clamped * ITEM_H, behavior: "smooth" });
      onSelect(items[clamped]);
    }
  };

  // Build the rendered list
  const renderItems = () => {
    const elements: React.ReactNode[] = [];
    // Top padding
    if (!loop) {
      for (let i = 0; i < pad; i++) {
        elements.push(<div key={`pad-top-${i}`} style={{ height: ITEM_H }} />);
      }
    }
    const count = loop ? REPEATS : 1;
    for (let block = 0; block < count; block++) {
      for (let i = 0; i < n; i++) {
        const item = items[i];
        const isSelected = item === selected;
        elements.push(
          <div
            key={`${block}-${i}`}
            onClick={() => handleItemClick(i)}
            className={cn(
              "flex items-center justify-center snap-center select-none transition-colors duration-100",
              isSelected
                ? "text-[#1e3a8a] font-bold text-lg"
                : "text-slate-400 text-sm hover:text-slate-600 cursor-pointer"
            )}
            style={{ height: ITEM_H }}
          >
            {display(item)}
          </div>
        );
      }
    }
    // Bottom padding
    if (!loop) {
      for (let i = 0; i < pad; i++) {
        elements.push(<div key={`pad-bot-${i}`} style={{ height: ITEM_H }} />);
      }
    }
    return elements;
  };

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      className="overflow-y-auto tp-no-scrollbar cursor-grab active:cursor-grabbing"
      style={{
        height: VISIBLE * ITEM_H,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        touchAction: "none",
      }}
    >
      {renderItems()}
    </div>
  );
}

export function TimePicker({ value, onChange, className, placeholder }: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const [typedValue, setTypedValue] = useState("");
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerInputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const [triggerValue, setTriggerValue] = useState("");
  const parsed = parse24(value || "09:00");
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState(parsed.period);

  const displayTime = value ? formatTime12(value) : "";

  useEffect(() => {
    if (document.activeElement !== triggerInputRef.current) {
      setTriggerValue(displayTime);
    }
  }, [displayTime]);

  useEffect(() => {
    const p = parse24(value || "09:00");
    setHour(p.hour);
    setMinute(p.minute);
    setPeriod(p.period);
  }, [value, open]);

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showInput]);

  useEffect(() => {
    if (open && anchorRef.current) {
      setAnchorWidth(anchorRef.current.offsetWidth);
    }
  }, [open]);

  const handleOk = () => {
    onChange(to24(hour, minute, period));
    setOpen(false);
    setShowInput(false);
  };

  const handleCancel = () => {
    const p = parse24(value || "09:00");
    setHour(p.hour);
    setMinute(p.minute);
    setPeriod(p.period);
    setOpen(false);
    setShowInput(false);
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const result = parseTypedTime(triggerValue);
      if (result) {
        onChange(to24(result.hour, result.minute, result.period));
      } else {
        setTriggerValue(displayTime);
      }
      triggerInputRef.current?.blur();
    } else if (e.key === "Escape") {
      setTriggerValue(displayTime);
      triggerInputRef.current?.blur();
    }
  };

  const handleTriggerBlur = () => {
    const result = parseTypedTime(triggerValue);
    if (result) {
      onChange(to24(result.hour, result.minute, result.period));
    } else {
      setTriggerValue(displayTime);
    }
  };

  const handleTypedSubmit = () => {
    const result = parseTypedTime(typedValue);
    if (result) {
      setHour(result.hour);
      setMinute(result.minute);
      setPeriod(result.period);
      setShowInput(false);
    }
  };

  const handleTypedKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleTypedSubmit();
    } else if (e.key === "Escape") {
      setShowInput(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setShowInput(false); }}>
      <style>{hideScrollbarStyle}</style>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className={cn("relative flex items-center", className)}>
          <input
            ref={triggerInputRef}
            value={triggerValue}
            onChange={(e) => setTriggerValue(e.target.value)}
            onKeyDown={handleTriggerKeyDown}
            onBlur={handleTriggerBlur}
            placeholder={placeholder || "Select time"}
            className="h-10 w-full rounded-md border border-input bg-background px-3 pr-9 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
          <PopoverTrigger asChild>
            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              <Clock className="w-4 h-4" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="p-0 rounded-xl shadow-xl" align="start" style={{ width: anchorWidth || undefined }}>
        {/* Typeable input */}
        <div className="px-4 pt-3 pb-1">
          {showInput ? (
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                onKeyDown={handleTypedKeyDown}
                onBlur={handleTypedSubmit}
                placeholder="e.g. 9:30 AM"
                className="h-8 text-sm text-center"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setTypedValue(`${hour}:${String(minute).padStart(2, "0")} ${period}`);
                setShowInput(true);
              }}
              className="w-full text-center text-lg font-semibold text-[#1e3a8a] hover:bg-[#1e3a8a]/5 rounded-md py-1 transition-colors"
            >
              {hour}:{String(minute).padStart(2, "0")} {period}
            </button>
          )}
        </div>

        {/* Scroll columns */}
        <div className="px-4 pt-1 pb-2">
          <div className="flex relative justify-center">
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-8 rounded-md bg-[#1e3a8a]/8 pointer-events-none z-0" />

            <div className="relative z-10 flex-1 max-w-[60px]">
              <ScrollColumn
                items={HOURS}
                selected={hour}
                onSelect={setHour}
              />
            </div>
            <div className="relative z-10 flex-1 max-w-[60px]">
              <ScrollColumn
                items={MINUTES}
                selected={minute}
                onSelect={setMinute}
                format={(v) => String(v).padStart(2, "0")}
              />
            </div>
            <div className="relative z-10 flex-1 max-w-[60px]">
              <ScrollColumn
                items={PERIODS}
                selected={period}
                onSelect={setPeriod}
                loop={false}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 pb-3 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-[#1e3a8a] text-xs font-semibold uppercase tracking-wide hover:bg-[#1e3a8a]/10"
          >
            Cancel
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOk}
            className="text-[#1e3a8a] text-xs font-semibold uppercase tracking-wide hover:bg-[#1e3a8a]/10"
          >
            OK
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
